import React, { useState, useEffect } from 'react';
import CameraCapture from '../components/CameraCapture';
import { CheckCircle, AlertCircle, Loader } from 'lucide-react';

// Public "apply remotely" form (/apply?key=…) for an applicant who can't come in.
// One standing link, copied from Personnel → Applicants. Submitting CREATES their
// applicant card (or adds to the one already on file for that mobile), marked
// "returned" so it shows with the red dot for review.
//
// Document labels are the ones the hire step already files into the driver's
// compliance record (driver-dms-sb ONBOARDING_DOC_TYPE / licence / citizenship).

const API = '/.netlify/functions/applicants-sb';

function dataUrlToDoc(label, dataUrl) {
  if (!dataUrl) return null;
  const comma = dataUrl.indexOf(',');
  if (comma < 0) return null;
  const contentType = (dataUrl.slice(0, comma).match(/:(.*?);/) || [])[1] || 'image/jpeg';
  return { label, photoBase64: dataUrl.slice(comma + 1), contentType };
}

const wrap = { maxWidth: 560, margin: '0 auto', padding: '20px 16px 64px' };
const card = { background: 'var(--hp-surface, #fff)', border: '1px solid var(--hp-border, #e5e7eb)', borderRadius: 12, padding: 16, marginBottom: 14 };
const label = { display: 'block', fontSize: '.8rem', fontWeight: 600, color: 'var(--hp-text, #111827)', marginBottom: 5 };
const input = { width: '100%', padding: '11px 12px', borderRadius: 8, border: '1px solid var(--hp-border, #d1d5db)', fontSize: '16px', boxSizing: 'border-box', background: 'var(--hp-bg, #fff)', color: 'var(--hp-text, #111)' };
const sectionTitle = { fontSize: '.7rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.06em', color: 'var(--hp-gold, #b8923a)', marginBottom: 12 };

function Field({ lbl, children, hint }) {
  return (
    <div style={{ marginBottom: 12 }}>
      <label style={label}>{lbl}</label>
      {children}
      {hint && <div style={{ fontSize: '.7rem', color: 'var(--hp-dim, #9ca3af)', marginTop: 4 }}>{hint}</div>}
    </div>
  );
}
function Choice({ value, current, onChange, children }) {
  const active = current === value;
  return (
    <button type="button" onClick={() => onChange(value)}
      style={{ flex: 1, padding: '11px 8px', borderRadius: 8, fontSize: '.85rem', fontWeight: 600, cursor: 'pointer',
        border: `1.5px solid ${active ? 'var(--hp-gold, #b8923a)' : 'var(--hp-border, #d1d5db)'}`,
        background: active ? 'var(--hp-gold, #b8923a)' : 'transparent', color: active ? '#fff' : 'var(--hp-text, #374151)' }}>
      {children}
    </button>
  );
}
const Photo = ({ value, set, guide = 'document' }) => (
  <CameraCapture nativeFirst guide={guide} maxEdge={1600} quality={0.85} value={value} onCapture={set} onClear={() => set(null)} />
);

export default function ApplyPage() {
  const key = new URLSearchParams(window.location.search).get('key') || '';
  const [loading, setLoading] = useState(true);
  const [linkError, setLinkError] = useState('');
  const [consentText, setConsentText] = useState('');
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const [f, setF] = useState({
    fullName: '', mobile: '', email: '', dateOfBirth: '', currentAddress: '',
    hasLicence: '', citizen: '', citizenProof: 'passport',
    passportNumber: '', passportExpiry: '', country: '', visaType: '', grantNumber: '', vevoConsent: false,
  });
  const set = (k, v) => setF(p => ({ ...p, [k]: v }));
  const [headshot, setHeadshot] = useState(null);
  const [idFront, setIdFront] = useState(null);
  const [idBack, setIdBack] = useState(null);
  const [passport, setPassport] = useState(null);
  const [certBack, setCertBack] = useState(null);
  const [visaDoc, setVisaDoc] = useState(null);

  useEffect(() => {
    if (!key) { setLinkError('This link is incomplete. Please use the exact link you were sent.'); setLoading(false); return; }
    fetch(API, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ action: 'getApplyForm', key }) })
      .then(r => r.json())
      .then(d => { if (!d || !d.success) setLinkError((d && d.error) || 'This link is not valid.'); else setConsentText(d.vevoConsentText || ''); })
      .catch(e => setLinkError(e.message))
      .finally(() => setLoading(false));
  }, [key]);

  const citizenPassport = f.citizen === 'yes' && f.citizenProof === 'passport';
  const citizenCert = f.citizen === 'yes' && f.citizenProof === 'certificate';
  const citizenBirth = f.citizen === 'yes' && f.citizenProof === 'birth';

  async function submit() {
    setError('');
    if (!f.fullName.trim()) return setError('Please enter your full name.');
    if (!f.mobile.trim()) return setError('Please enter your mobile number.');
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(f.email.trim())) return setError('Please enter a valid email address.');
    if (!f.dateOfBirth) return setError('Please enter your date of birth.');
    if (f.currentAddress.trim().length < 8) return setError('Please enter your current residential address.');
    if (!headshot) return setError('Please add a head-and-shoulders photo of yourself.');
    if (!f.hasLicence) return setError('Please tell us whether you have an Australian driver’s licence.');
    if (!idFront) return setError(f.hasLicence === 'yes' ? 'Please add a photo of the front of your driver’s licence.' : 'Please add a photo of your ID.');
    if (f.hasLicence === 'yes' && !idBack) return setError('Please add the back of your licence. For a digital licence, a screenshot of the bottom showing the card number.');
    if (f.hasLicence === 'no' && !idBack) return setError('Please add a photo of the back of your photo ID.');
    if (!f.citizen) return setError('Please tell us whether you are an Australian citizen.');
    if (citizenPassport && !f.passportNumber.trim()) return setError('Please enter your passport number.');
    if ((citizenPassport || f.citizen === 'no') && !f.passportExpiry) return setError('Please enter your passport expiry date.');
    if (citizenCert && !passport) return setError('Please add a photo of the front of your citizenship certificate.');
    if (citizenCert && !certBack) return setError('Please add a photo of the back of your citizenship certificate (the certificate number is printed there).');
    if (citizenBirth && !passport) return setError('Please add a photo of your birth certificate.');
    if (citizenPassport && !passport) return setError('Please add a photo of your passport’s photo page.');
    if (f.citizen === 'no') {
      if (!f.country.trim() || !f.passportNumber.trim()) return setError('Please enter your country of citizenship and passport number.');
      if (!f.visaType.trim() || !f.grantNumber.trim()) return setError('Please enter your visa type and visa grant number.');
      if (!passport) return setError('Please add a photo of your passport’s photo page.');
      if (!visaDoc) return setError('Please add your visa grant notice (a photo or screenshot of the email or letter).');
      if (!f.vevoConsent) return setError('Please tick the box to agree to the work-rights check.');
    }

    const documents = [
      dataUrlToDoc('Head-shot', headshot),
      dataUrlToDoc(f.hasLicence === 'yes' ? "Driver's Licence — Front" : 'Photo ID — Front', idFront),
      dataUrlToDoc(f.hasLicence === 'yes' ? "Driver's Licence — Back" : 'Photo ID — Back', idBack),
      dataUrlToDoc(citizenCert || citizenBirth ? 'Passport / Birth Certificate' : 'Passport', passport),
      citizenCert ? dataUrlToDoc('Australian Citizenship — Back', certBack) : null,
      f.citizen === 'no' ? dataUrlToDoc('Visa Grant Notice', visaDoc) : null,
    ].filter(Boolean);
    // Only the answers that apply to the path they took.
    const answers = { ...f };
    if (f.citizen === 'yes') { delete answers.country; delete answers.visaType; delete answers.grantNumber; delete answers.vevoConsent; }
    if (citizenCert || citizenBirth) { delete answers.passportNumber; delete answers.passportExpiry; }
    if (f.citizen !== 'yes') delete answers.citizenProof;

    setSubmitting(true);
    try {
      const res = await fetch(API, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ action: 'submitApplication', key, answers, documents }) });
      const d = await res.json().catch(() => null);
      if (d && d.success) { setDone(true); window.scrollTo(0, 0); }
      else setError((d && d.error) || (res.status === 413 ? 'Your photos are too large to send together. Try retaking them.' : 'Could not send. Please check your connection and try again.'));
    } catch (e) { setError(e.message); }
    finally { setSubmitting(false); }
  }

  if (loading) return <div style={{ ...wrap, textAlign: 'center', paddingTop: 80, color: 'var(--hp-dim,#9ca3af)' }}><Loader size={28} style={{ animation: 'spin 1s linear infinite' }} /><div style={{ marginTop: 10 }}>Loading…</div></div>;

  if (linkError) return (
    <div style={{ ...wrap, paddingTop: 60 }}>
      <div style={{ ...card, textAlign: 'center' }}>
        <AlertCircle size={32} color="#dc2626" />
        <div style={{ fontWeight: 700, marginTop: 10 }}>Link not valid</div>
        <div style={{ color: 'var(--hp-dim,#6b7280)', fontSize: '.85rem', marginTop: 6 }}>{linkError}</div>
      </div>
    </div>
  );

  if (done) return (
    <div style={{ ...wrap, paddingTop: 60 }}>
      <div style={{ ...card, textAlign: 'center' }}>
        <CheckCircle size={40} color="#16a34a" />
        <div style={{ fontWeight: 700, fontSize: '1.1rem', marginTop: 12 }}>Thank you, {f.fullName.split(' ')[0]}!</div>
        <div style={{ color: 'var(--hp-dim,#6b7280)', fontSize: '.9rem', marginTop: 8 }}>Your details have been sent to Hollypaul. We’ll be in touch about the next steps.</div>
      </div>
    </div>
  );

  return (
    <div style={wrap}>
      <div style={{ textAlign: 'center', marginBottom: 18 }}>
        <div style={{ fontFamily: 'var(--font-display, serif)', fontWeight: 800, fontSize: '1.4rem', letterSpacing: '.04em', color: 'var(--hp-gold, #b8923a)' }}>HOLLYPAUL</div>
        <div style={{ fontSize: '.95rem', fontWeight: 700, marginTop: 8 }}>Apply to work with Hollypaul</div>
        <div style={{ fontSize: '.82rem', color: 'var(--hp-dim,#6b7280)', marginTop: 4 }}>Fill in your details and add photos of your ID so we can start your application. It takes about five minutes. Have your licence and passport handy.</div>
      </div>

      <div style={card}>
        <div style={sectionTitle}>Your details</div>
        <Field lbl="Full name" hint="As it appears on your ID."><input style={input} value={f.fullName} onChange={e => set('fullName', e.target.value)} autoComplete="name" /></Field>
        <Field lbl="Mobile"><input style={input} type="tel" value={f.mobile} onChange={e => set('mobile', e.target.value)} autoComplete="tel" placeholder="04xx xxx xxx" /></Field>
        <Field lbl="Email"><input style={input} type="email" value={f.email} onChange={e => set('email', e.target.value)} autoComplete="email" /></Field>
        <Field lbl="Date of birth"><input style={input} type="date" value={f.dateOfBirth} onChange={e => set('dateOfBirth', e.target.value)} /></Field>
        <Field lbl="Current residential address"><input style={input} value={f.currentAddress} onChange={e => set('currentAddress', e.target.value)} autoComplete="street-address" placeholder="Unit / street / suburb / state / postcode" /></Field>
        <Field lbl="A photo of you (Head and Shoulders)" hint="Facing the camera, against a plain background. Australia Post uses it for your security pass.">
          <Photo value={headshot} set={setHeadshot} guide="portrait" />
        </Field>
      </div>

      <div style={card}>
        <div style={sectionTitle}>Photo ID</div>
        <Field lbl="Do you have an Australian driver’s licence?">
          <div style={{ display: 'flex', gap: 8 }}>
            <Choice value="yes" current={f.hasLicence} onChange={v => set('hasLicence', v)}>Yes</Choice>
            <Choice value="no" current={f.hasLicence} onChange={v => set('hasLicence', v)}>No</Choice>
          </div>
        </Field>
        {f.hasLicence && (
          <>
            <Field lbl={f.hasLicence === 'yes' ? 'Front of your licence' : 'Front of your photo ID'} hint={f.hasLicence === 'yes' ? 'The plastic card, or a screenshot of your digital licence.' : 'Any government photo ID, e.g. a proof-of-age card.'}>
              <Photo value={idFront} set={setIdFront} />
            </Field>
            <Field lbl={f.hasLicence === 'yes' ? 'Back of your licence' : 'Back of your photo ID'} hint={f.hasLicence === 'yes' ? 'For a digital licence, scroll down and screenshot the bottom of it, where the card number is shown.' : undefined}>
              <Photo value={idBack} set={setIdBack} />
            </Field>
          </>
        )}
      </div>

      <div style={card}>
        <div style={sectionTitle}>Citizenship</div>
        <Field lbl="Are you an Australian citizen?">
          <div style={{ display: 'flex', gap: 8 }}>
            <Choice value="yes" current={f.citizen} onChange={v => set('citizen', v)}>Yes</Choice>
            <Choice value="no" current={f.citizen} onChange={v => set('citizen', v)}>No</Choice>
          </div>
        </Field>

        {f.citizen === 'yes' && (
          <>
            <div style={{ fontSize: '.8rem', color: 'var(--hp-dim,#6b7280)', marginBottom: 10 }}>We need proof of citizenship. An Australian passport is best.</div>
            <Field lbl="What can you provide?">
              <div style={{ display: 'flex', gap: 8 }}>
                <Choice value="passport" current={f.citizenProof} onChange={v => set('citizenProof', v)}>Australian passport</Choice>
                <Choice value="certificate" current={f.citizenProof} onChange={v => set('citizenProof', v)}>Citizenship certificate</Choice>
                <Choice value="birth" current={f.citizenProof} onChange={v => set('citizenProof', v)}>Birth certificate</Choice>
              </div>
            </Field>
          </>
        )}

        {(citizenPassport || f.citizen === 'no') && (
          <>
            {f.citizen === 'no' && (
              <Field lbl="Country of citizenship"><input style={input} value={f.country} onChange={e => set('country', e.target.value)} /></Field>
            )}
            <Field lbl="Passport number" hint="Exactly as printed on your passport.">
              <input style={input} value={f.passportNumber} onChange={e => set('passportNumber', e.target.value.toUpperCase())} autoCapitalize="characters" />
            </Field>
            <Field lbl="Passport expiry date"><input style={input} type="date" value={f.passportExpiry} onChange={e => set('passportExpiry', e.target.value)} /></Field>
            <Field lbl="Photo of your passport’s photo page" hint="The page with your photo and details. Make sure every corner is in the picture.">
              <Photo value={passport} set={setPassport} />
            </Field>
          </>
        )}

        {citizenCert && (
          <>
            <Field lbl="Front of your citizenship certificate">
              <Photo value={passport} set={setPassport} />
            </Field>
            <Field lbl="Back of your citizenship certificate" hint="The certificate number is printed on the back.">
              <Photo value={certBack} set={setCertBack} />
            </Field>
          </>
        )}

        {citizenBirth && (
          <Field lbl="Your Australian birth certificate" hint="The full certificate, with every corner in the picture.">
            <Photo value={passport} set={setPassport} />
          </Field>
        )}

        {f.citizen === 'no' && (
          <>
            <Field lbl="Visa type"><input style={input} value={f.visaType} onChange={e => set('visaType', e.target.value)} placeholder="e.g. Student 500, Working Holiday 417, Permanent Resident" /></Field>
            <Field lbl="Visa grant number" hint="On your visa grant email from the Department of Home Affairs."><input style={input} value={f.grantNumber} onChange={e => set('grantNumber', e.target.value)} /></Field>
            <Field lbl="Visa grant notice" hint="A photo or screenshot of your visa grant email or letter.">
              <Photo value={visaDoc} set={setVisaDoc} />
            </Field>
            <label style={{ display: 'flex', gap: 10, alignItems: 'flex-start', fontSize: '.84rem', lineHeight: 1.45, marginTop: 6, cursor: 'pointer' }}>
              <input type="checkbox" checked={!!f.vevoConsent} onChange={e => set('vevoConsent', e.target.checked)} style={{ marginTop: 3, width: 18, height: 18, flexShrink: 0 }} />
              <span>{consentText || 'I consent to Hollypaul checking my visa and work rights with the Department of Home Affairs (VEVO), using my passport details, through its provider vSure.'}</span>
            </label>
          </>
        )}
      </div>

      <div style={{ ...card, background: 'var(--hp-bg, #f9fafb)' }}>
        <div style={sectionTitle}>Your privacy &amp; security</div>
        <div style={{ fontSize: '.78rem', color: 'var(--hp-dim, #6b7280)', lineHeight: 1.5 }}>
          Everything you send is <strong>encrypted in transit and stored encrypted</strong>, in private storage, never on
          a public or shareable link. Only Hollypaul’s <strong>authorised admins can open your documents, and only after
          signing in</strong>. We collect this only to assess your application and meet our obligations under the Australian
          Privacy Act, and you can ask us to access or delete it at any time.
        </div>
      </div>

      {error && <div style={{ ...card, background: '#fef2f2', border: '1px solid #fca5a5', color: '#b91c1c', fontSize: '.85rem', display: 'flex', gap: 8, alignItems: 'flex-start' }}><AlertCircle size={18} style={{ flexShrink: 0, marginTop: 1 }} />{error}</div>}

      <button type="button" onClick={submit} disabled={submitting}
        style={{ width: '100%', padding: '15px', borderRadius: 10, border: 'none', background: 'var(--hp-gold, #b8923a)', color: '#fff', fontSize: '1rem', fontWeight: 700, cursor: 'pointer', opacity: submitting ? 0.6 : 1 }}>
        {submitting ? 'Sending…' : 'Send my application'}
      </button>
      <div style={{ textAlign: 'center', fontSize: '.7rem', color: 'var(--hp-dim,#9ca3af)', marginTop: 12 }}>Your information is sent securely to Hollypaul Recruitment Pty Ltd.</div>
    </div>
  );
}
